import React, { useState } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { ShieldCheck, RefreshCw, CheckCircle2 } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import useGuarantors from "hooks/useGuarantors";
import { Guarantor, verifyGuarantor } from "services/guarantor.service";

const LIMIT = 20;

const CONSENT_COLORS: Record<string, string> = {
  Approved: "bg-green-50 text-green-700",
  Denied: "bg-red-50 text-red-700",
  Pending: "bg-amber-50 text-amber-700",
};

const STATUS_COLORS: Record<string, string> = {
  Verified: "bg-green-50 text-green-700",
  Responded: "bg-blue-50 text-blue-700",
  "Awaiting Response": "bg-gray-100 text-gray-600",
};

const Guarantors = () => {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: response, isLoading, isError, refetch } = useGuarantors({ page, limit: LIMIT });

  const guarantors: Guarantor[] = response?.data?.data ?? [];
  const total = response?.data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const { mutate: doVerify, isLoading: verifying } = useMutation(
    (id: string) => verifyGuarantor(id),
    {
      onSuccess: () => {
        toast.success("Guarantor verified");
        queryClient.invalidateQueries("guarantors");
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message ?? "Failed to verify");
      },
    }
  );

  return (
    <React.Fragment>
      <BreadCrumb title="Guarantors" pageTitle="Workers" />
      <ToastContainer closeButton={false} limit={1} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-3">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
          <span className="sm:ml-auto text-sm text-gray-500">{total} total</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Worker</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Guarantor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Relationship</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Consent</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Submitted</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={7} label="Couldn't load guarantors" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : guarantors.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <ShieldCheck className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No guarantors found</p>
                  </td>
                </tr>
              ) : (
                guarantors.map((g) => (
                  <tr key={g._id} className="hover:bg-gray-50 transition-colors">
                    <td data-label="Worker" data-cell="block" className="px-4 py-3">
                      {g.user ? (
                        <>
                          <p className="font-medium text-gray-900">
                            {g.user.firstName} {g.user.lastName}
                          </p>
                          <p className="text-xs text-gray-400">{g.user.phoneNumber}</p>
                        </>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td data-label="Guarantor" data-cell="title" className="px-4 py-3">
                      <p className="font-medium text-gray-900">{g.fullName}</p>
                      {g.phone && <p className="text-xs text-gray-400">{g.phone}</p>}
                    </td>
                    <td data-label="Relationship" className="px-4 py-3 text-gray-600">
                      {g.relationship || "—"}
                    </td>
                    <td data-label="Consent" className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${CONSENT_COLORS[g.consent] ?? "bg-gray-100 text-gray-600"}`}>
                        {g.consent}
                      </span>
                    </td>
                    <td data-label="Status" className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[g.status] ?? "bg-gray-100 text-gray-600"}`}>
                        {g.status}
                      </span>
                    </td>
                    <td data-label="Submitted" className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(g.createdAt).toLocaleDateString()}
                    </td>
                    <td data-label="Action" className="px-4 py-3">
                      {g.status === "Responded" ? (
                        <button
                          disabled={verifying}
                          onClick={() => doVerify(g._id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 transition-colors disabled:opacity-50"
                        >
                          <CheckCircle2 className="size-3" /> Verify
                        </button>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} · {total} total
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Guarantors;
